<template>
  <div v-if="comparisonResult">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h4 class="mb-0">
        Comparison Results
      </h4>
      <b-button-group>
        <b-button
          :variant="activeTab === 'normal' ? 'primary' : 'outline-primary'"
          size="sm"
          @click="activeTab = 'normal'"
        >
          Normal
        </b-button>
        <b-button
          :variant="activeTab === 'rounded' ? 'primary' : 'outline-primary'"
          size="sm"
          @click="activeTab = 'rounded'"
        >
          Rounded
        </b-button>
      </b-button-group>
    </div>

    <!-- Combined Reports Wrapper -->
    <div class="combined-wrapper mb-2">
      <div class="d-flex justify-content-end mb-1">
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="printingCombined"
          @click="handlePrintCombined"
        >
          <b-spinner
            v-if="printingCombined"
            small
            class="mr-1"
          />
          <feather-icon
            v-else
            icon="PrinterIcon"
            class="mr-1"
          />
          Print Combined
        </b-button>
      </div>

      <!-- Report 1: Supplier Debit + VDPL Credit Combined -->
      <b-card class="mb-1 result-card combined-report">
        <template #header>
          <h5 class="mb-0">
            1. Supplier Debit + VDPL Credit (Combined Unmatched)
          </h5>
          <small class="text-muted">
            Supplier Unmatched: {{ visibleCompanyDebitUnmatchedCount }} |
            VDPL Unmatched: {{ visibleVdplCreditUnmatchedCount }} |
            <strong>Total: {{ visibleCompanyDebitUnmatchedCount + visibleVdplCreditUnmatchedCount }}</strong>
          </small>
        </template>
        <div
          v-if="visibleCombinedCreditDebitRows.length > 0 || report1MatchedPairs.length > 0"
          class="table-responsive"
        >
          <!-- Summation Totals Bar with Hide/Reset on right -->
          <div class="summation-bar d-flex justify-content-between align-items-center mb-1 px-1 py-half">
            <div class="d-flex align-items-center">
              <span class="font-weight-bold mr-1">Supplier Total:</span>
              <span
                class="summation-value"
                :class="{
                  'text-danger font-weight-bold': report1SupplierTotal > 0 && !report1Matched,
                  'text-success font-weight-bold': report1Matched
                }"
              >
                {{ report1SupplierTotal > 0 ? formatAmount(report1SupplierTotal) : '0.00' }}
              </span>
            </div>
            <div class="d-flex align-items-center">
              <span class="font-weight-bold mr-1">VDPL Total:</span>
              <span
                class="summation-value"
                :class="{
                  'text-danger font-weight-bold': report1VdplTotal > 0 && !report1Matched,
                  'text-success font-weight-bold': report1Matched
                }"
              >
                {{ report1VdplTotal > 0 ? formatAmount(report1VdplTotal) : '0.00' }}
              </span>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                :variant="report1HideEnabled ? 'success' : 'secondary'"
                size="sm"
                :disabled="!report1HideEnabled"
                class="mr-half"
                @click="hideMatchedReport1"
              >
                <feather-icon
                  icon="EyeOffIcon"
                  class="mr-half"
                />
                Hide
              </b-button>
              <b-button
                variant="outline-warning"
                size="sm"
                @click="resetReport1"
              >
                <feather-icon icon="RotateCcwIcon" />
              </b-button>
            </div>
          </div>

          <div class="table-scroll-container">
            <table class="table table-bordered table-sm compact-table mb-0">
              <thead>
                <tr>
                  <th
                    class="bg-warning-light"
                    colspan="4"
                  >
                    Supplier Debit (Report 5)
                  </th>
                  <th
                    class="bg-info-light"
                    colspan="4"
                  >
                    VDPL Credit (Report 3)
                  </th>
                </tr>
                <tr>
                  <th class="bg-warning-light">
                    Excel Row
                  </th>
                  <th class="bg-warning-light text-right">
                    Debit
                  </th>
                  <th class="bg-warning-light th-action" />
                  <th class="bg-warning-light">
                    Date
                  </th>
                  <th class="bg-info-light">
                    Excel Row
                  </th>
                  <th class="bg-info-light text-right">
                    Credit
                  </th>
                  <th class="bg-info-light th-action" />
                  <th class="bg-info-light">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in visibleCombinedCreditDebitRows"
                  :key="`r1-${index}`"
                >
                  <td :class="getReport1CompanyCellClass(row.company_row_id)">
                    {{ row.company_excel_row }}
                  </td>
                  <td
                    class="text-right"
                    :class="getReport1CompanyCellClass(row.company_row_id)"
                  >
                    {{ row.company_debit ? formatAmount(row.company_debit) : '' }}
                  </td>
                  <!-- Supplier side action (between Debit & Date) -->
                  <td class="text-center action-cell">
                    <span
                      v-if="row.company_debit && !report1Matched"
                      class="action-icon"
                      :class="{ 'text-danger': isReport1CompanySelected(row.company_row_id) }"
                      @click="toggleReport1Company(row)"
                    >
                      <feather-icon
                        :icon="isReport1CompanySelected(row.company_row_id) ? 'MinusCircleIcon' : 'PlusCircleIcon'"
                        size="16"
                      />
                    </span>
                  </td>
                  <td :class="getReport1CompanyCellClass(row.company_row_id)">
                    {{ row.company_date }}
                  </td>
                  <td :class="getReport1VdplCellClass(row.vdpl_row_id)">
                    {{ row.vdpl_excel_row }}
                  </td>
                  <td
                    class="text-right"
                    :class="getReport1VdplCellClass(row.vdpl_row_id)"
                  >
                    {{ row.vdpl_credit ? formatAmount(row.vdpl_credit) : '' }}
                  </td>
                  <!-- VDPL side action (between Credit & Date) -->
                  <td class="text-center action-cell">
                    <span
                      v-if="row.vdpl_credit && !report1Matched"
                      class="action-icon"
                      :class="{ 'text-danger': isReport1VdplSelected(row.vdpl_row_id) }"
                      @click="toggleReport1Vdpl(row)"
                    >
                      <feather-icon
                        :icon="isReport1VdplSelected(row.vdpl_row_id) ? 'MinusCircleIcon' : 'PlusCircleIcon'"
                        size="16"
                      />
                    </span>
                  </td>
                  <td :class="getReport1VdplCellClass(row.vdpl_row_id)">
                    {{ row.vdpl_date }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Matched Pairs Dropdown -->
          <div
            v-if="report1MatchedPairs.length > 0"
            class="matched-pairs-section mt-1"
          >
            <div
              class="matched-pairs-header d-flex align-items-center cursor-pointer py-half px-1"
              @click="report1PairsExpanded = !report1PairsExpanded"
            >
              <feather-icon
                :icon="report1PairsExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="16"
                class="mr-half"
              />
              <span class="font-weight-bold text-success">
                Matched Pairs ({{ report1MatchedPairs.length }})
              </span>
            </div>
            <div v-if="report1PairsExpanded">
              <div
                v-for="(pair, pIdx) in report1MatchedPairs"
                :key="`mp1-${pIdx}`"
                class="matched-pair-card mb-half"
              >
                <div class="pair-label">
                  Pair {{ pIdx + 1 }} — {{ formatAmount(pair.total) }}
                </div>
                <table class="table table-bordered table-sm compact-table mb-0 matched-table">
                  <thead>
                    <tr>
                      <th
                        class="bg-warning-light"
                        colspan="3"
                      >
                        Supplier Debit
                      </th>
                      <th
                        class="bg-info-light"
                        colspan="3"
                      >
                        VDPL Credit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(mrow, mIdx) in pair.rows"
                      :key="`mp1r-${pIdx}-${mIdx}`"
                      class="matched-row"
                    >
                      <td class="text-success">
                        {{ mrow.company_excel_row }}
                      </td>
                      <td class="text-right text-success">
                        {{ mrow.company_debit ? formatAmount(mrow.company_debit) : '' }}
                      </td>
                      <td class="text-success">
                        {{ mrow.company_date }}
                      </td>
                      <td class="text-success">
                        {{ mrow.vdpl_excel_row }}
                      </td>
                      <td class="text-right text-success">
                        {{ mrow.vdpl_credit ? formatAmount(mrow.vdpl_credit) : '' }}
                      </td>
                      <td class="text-success">
                        {{ mrow.vdpl_date }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-center text-success py-3"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="24"
            class="mr-1"
          /> All entries matched!
        </div>
      </b-card>

      <!-- Report 2: Supplier Credit + VDPL Debit Combined -->
      <b-card class="mb-0 result-card combined-report">
        <template #header>
          <h5 class="mb-0">
            2. Supplier Credit + VDPL Debit (Combined Unmatched)
          </h5>
          <small class="text-muted">
            Supplier Unmatched: {{ visibleCompanyCreditUnmatchedCount }} |
            VDPL Unmatched: {{ visibleVdplDebitUnmatchedCount }} |
            <strong>Total: {{ visibleCompanyCreditUnmatchedCount + visibleVdplDebitUnmatchedCount }}</strong>
          </small>
        </template>
        <div
          v-if="visibleCombinedDebitCreditRows.length > 0 || report2MatchedPairs.length > 0"
          class="table-responsive"
        >
          <!-- Summation Totals Bar with Hide/Reset on right -->
          <div class="summation-bar d-flex justify-content-between align-items-center mb-1 px-1 py-half">
            <div class="d-flex align-items-center">
              <span class="font-weight-bold mr-1">Supplier Total:</span>
              <span
                class="summation-value"
                :class="{
                  'text-danger font-weight-bold': report2SupplierTotal > 0 && !report2Matched,
                  'text-success font-weight-bold': report2Matched
                }"
              >
                {{ report2SupplierTotal > 0 ? formatAmount(report2SupplierTotal) : '0.00' }}
              </span>
            </div>
            <div class="d-flex align-items-center">
              <span class="font-weight-bold mr-1">VDPL Total:</span>
              <span
                class="summation-value"
                :class="{
                  'text-danger font-weight-bold': report2VdplTotal > 0 && !report2Matched,
                  'text-success font-weight-bold': report2Matched
                }"
              >
                {{ report2VdplTotal > 0 ? formatAmount(report2VdplTotal) : '0.00' }}
              </span>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                :variant="report2HideEnabled ? 'success' : 'secondary'"
                size="sm"
                :disabled="!report2HideEnabled"
                class="mr-half"
                @click="hideMatchedReport2"
              >
                <feather-icon
                  icon="EyeOffIcon"
                  class="mr-half"
                />
                Hide
              </b-button>
              <b-button
                variant="outline-warning"
                size="sm"
                @click="resetReport2"
              >
                <feather-icon icon="RotateCcwIcon" />
              </b-button>
            </div>
          </div>

          <div class="table-scroll-container">
            <table class="table table-bordered table-sm compact-table mb-0">
              <thead>
                <tr>
                  <th
                    class="bg-warning-light"
                    colspan="4"
                  >
                    Supplier Credit (Report 6)
                  </th>
                  <th
                    class="bg-info-light"
                    colspan="4"
                  >
                    VDPL Debit (Report 4)
                  </th>
                </tr>
                <tr>
                  <th class="bg-warning-light">
                    Excel Row
                  </th>
                  <th class="bg-warning-light text-right">
                    Credit
                  </th>
                  <th class="bg-warning-light th-action" />
                  <th class="bg-warning-light">
                    Date
                  </th>
                  <th class="bg-info-light">
                    Excel Row
                  </th>
                  <th class="bg-info-light text-right">
                    Debit
                  </th>
                  <th class="bg-info-light th-action" />
                  <th class="bg-info-light">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in visibleCombinedDebitCreditRows"
                  :key="`r2-${index}`"
                >
                  <td :class="getReport2CompanyCellClass(row.company_row_id)">
                    {{ row.company_excel_row }}
                  </td>
                  <td
                    class="text-right"
                    :class="getReport2CompanyCellClass(row.company_row_id)"
                  >
                    {{ row.company_credit ? formatAmount(row.company_credit) : '' }}
                  </td>
                  <!-- Supplier side action (between Credit & Date) -->
                  <td class="text-center action-cell">
                    <span
                      v-if="row.company_credit && !report2Matched"
                      class="action-icon"
                      :class="{ 'text-danger': isReport2CompanySelected(row.company_row_id) }"
                      @click="toggleReport2Company(row)"
                    >
                      <feather-icon
                        :icon="isReport2CompanySelected(row.company_row_id) ? 'MinusCircleIcon' : 'PlusCircleIcon'"
                        size="16"
                      />
                    </span>
                  </td>
                  <td :class="getReport2CompanyCellClass(row.company_row_id)">
                    {{ row.company_date }}
                  </td>
                  <td :class="getReport2VdplCellClass(row.vdpl_row_id)">
                    {{ row.vdpl_excel_row }}
                  </td>
                  <td
                    class="text-right"
                    :class="getReport2VdplCellClass(row.vdpl_row_id)"
                  >
                    {{ row.vdpl_debit ? formatAmount(row.vdpl_debit) : '' }}
                  </td>
                  <!-- VDPL side action (between Debit & Date) -->
                  <td class="text-center action-cell">
                    <span
                      v-if="row.vdpl_debit && !report2Matched"
                      class="action-icon"
                      :class="{ 'text-danger': isReport2VdplSelected(row.vdpl_row_id) }"
                      @click="toggleReport2Vdpl(row)"
                    >
                      <feather-icon
                        :icon="isReport2VdplSelected(row.vdpl_row_id) ? 'MinusCircleIcon' : 'PlusCircleIcon'"
                        size="16"
                      />
                    </span>
                  </td>
                  <td :class="getReport2VdplCellClass(row.vdpl_row_id)">
                    {{ row.vdpl_date }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Matched Pairs Dropdown -->
          <div
            v-if="report2MatchedPairs.length > 0"
            class="matched-pairs-section mt-1"
          >
            <div
              class="matched-pairs-header d-flex align-items-center cursor-pointer py-half px-1"
              @click="report2PairsExpanded = !report2PairsExpanded"
            >
              <feather-icon
                :icon="report2PairsExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="16"
                class="mr-half"
              />
              <span class="font-weight-bold text-success">
                Matched Pairs ({{ report2MatchedPairs.length }})
              </span>
            </div>
            <div v-if="report2PairsExpanded">
              <div
                v-for="(pair, pIdx) in report2MatchedPairs"
                :key="`mp2-${pIdx}`"
                class="matched-pair-card mb-half"
              >
                <div class="pair-label">
                  Pair {{ pIdx + 1 }} — {{ formatAmount(pair.total) }}
                </div>
                <table class="table table-bordered table-sm compact-table mb-0 matched-table">
                  <thead>
                    <tr>
                      <th
                        class="bg-warning-light"
                        colspan="3"
                      >
                        Supplier Credit
                      </th>
                      <th
                        class="bg-info-light"
                        colspan="3"
                      >
                        VDPL Debit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(mrow, mIdx) in pair.rows"
                      :key="`mp2r-${pIdx}-${mIdx}`"
                      class="matched-row"
                    >
                      <td class="text-success">
                        {{ mrow.company_excel_row }}
                      </td>
                      <td class="text-right text-success">
                        {{ mrow.company_credit ? formatAmount(mrow.company_credit) : '' }}
                      </td>
                      <td class="text-success">
                        {{ mrow.company_date }}
                      </td>
                      <td class="text-success">
                        {{ mrow.vdpl_excel_row }}
                      </td>
                      <td class="text-right text-success">
                        {{ mrow.vdpl_debit ? formatAmount(mrow.vdpl_debit) : '' }}
                      </td>
                      <td class="text-success">
                        {{ mrow.vdpl_date }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="text-center text-success py-3"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="24"
            class="mr-1"
          /> All entries matched!
        </div>
      </b-card>
    </div>

    <!-- Print button for Reports 3-6 -->
    <div class="d-flex justify-content-end mb-1">
      <b-button
        variant="outline-primary"
        size="sm"
        :disabled="printingReports"
        @click="handlePrintReports"
      >
        <b-spinner
          v-if="printingReports"
          small
          class="mr-1"
        />
        <feather-icon
          v-else
          icon="PrinterIcon"
          class="mr-1"
        />
        Print Reports 3-6
      </b-button>
    </div>

    <!-- Report 3: VDPL Credit vs Supplier Debit -->
    <b-card
      v-if="activeData.my_credit_vs_company_debit"
      class="mb-2 result-card"
    >
      <template #header>
        <h5 class="mb-0">
          3. VDPL Credit vs Supplier Debit
        </h5>
        <small class="text-muted">
          Total: {{ activeData.my_credit_vs_company_debit.total }} |
          Matched: {{ activeData.my_credit_vs_company_debit.matched }} |
          <span class="text-danger font-weight-bold">Unmatched: {{ activeData.my_credit_vs_company_debit.unmatched }}</span>
        </small>
      </template>
      <div
        v-if="activeData.my_credit_vs_company_debit.unmatched > 0"
        class="table-responsive"
      >
        <table class="table table-bordered table-sm compact-table mb-0">
          <thead>
            <tr>
              <th>Excel Row</th>
              <th>Date</th>
              <th>Particulars</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in activeData.my_credit_vs_company_debit.unmatched_entries"
              :key="`r3-${index}`"
            >
              <td>{{ row.excel_row }}</td>
              <td>{{ row.Date || '' }}</td>
              <td>{{ row.Particulars }}</td>
              <td class="text-right">
                {{ row.Debit ? formatAmount(row.Debit) : '' }}
              </td>
              <td class="text-right">
                {{ row.Credit ? formatAmount(row.Credit) : '' }}
              </td>
              <td class="text-right">
                {{ row.Balance ? formatAmount(row.Balance) : '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else
        class="text-center text-success py-3"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="24"
          class="mr-1"
        /> All entries matched!
      </div>
    </b-card>

    <!-- Report 4: VDPL Debit vs Supplier Credit -->
    <b-card
      v-if="activeData.my_debit_vs_company_credit"
      class="mb-2 result-card"
    >
      <template #header>
        <h5 class="mb-0">
          4. VDPL Debit vs Supplier Credit
        </h5>
        <small class="text-muted">
          Total: {{ activeData.my_debit_vs_company_credit.total }} |
          Matched: {{ activeData.my_debit_vs_company_credit.matched }} |
          <span class="text-danger font-weight-bold">Unmatched: {{ activeData.my_debit_vs_company_credit.unmatched }}</span>
        </small>
      </template>
      <div
        v-if="activeData.my_debit_vs_company_credit.unmatched > 0"
        class="table-responsive"
      >
        <table class="table table-bordered table-sm compact-table mb-0">
          <thead>
            <tr>
              <th>Excel Row</th>
              <th>Date</th>
              <th>Particulars</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in activeData.my_debit_vs_company_credit.unmatched_entries"
              :key="`r4-${index}`"
            >
              <td>{{ row.excel_row }}</td>
              <td>{{ row.Date || '' }}</td>
              <td>{{ row.Particulars }}</td>
              <td class="text-right">
                {{ row.Debit ? formatAmount(row.Debit) : '' }}
              </td>
              <td class="text-right">
                {{ row.Credit ? formatAmount(row.Credit) : '' }}
              </td>
              <td class="text-right">
                {{ row.Balance ? formatAmount(row.Balance) : '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else
        class="text-center text-success py-3"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="24"
          class="mr-1"
        /> All entries matched!
      </div>
    </b-card>

    <!-- Report 5: Supplier Debit vs VDPL Credit -->
    <b-card
      v-if="activeData.company_debit_vs_my_credit"
      class="mb-2 result-card"
    >
      <template #header>
        <h5 class="mb-0">
          5. Supplier Debit vs VDPL Credit
        </h5>
        <small class="text-muted">
          Total: {{ activeData.company_debit_vs_my_credit.total }} |
          Matched: {{ activeData.company_debit_vs_my_credit.matched }} |
          <span class="text-danger font-weight-bold">Unmatched: {{ activeData.company_debit_vs_my_credit.unmatched }}</span>
        </small>
      </template>
      <div
        v-if="activeData.company_debit_vs_my_credit.unmatched > 0"
        class="table-responsive"
      >
        <table class="table table-bordered table-sm compact-table mb-0">
          <thead>
            <tr>
              <th>Excel Row</th>
              <th>Reference</th>
              <th>Date</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Balance</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in activeData.company_debit_vs_my_credit.unmatched_entries"
              :key="`r5-${index}`"
            >
              <td>{{ row.excel_row }}</td>
              <td>{{ getCompanyReference(row) }}</td>
              <td>{{ getCompanyDate(row) }}</td>
              <td class="text-right">
                {{ row.debit ? formatAmount(row.debit) : '' }}
              </td>
              <td class="text-right">
                {{ row.credit ? formatAmount(row.credit) : '' }}
              </td>
              <td class="text-right">
                {{ row['4'] || '' }}
              </td>
              <td>{{ row['5'] || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else
        class="text-center text-success py-3"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="24"
          class="mr-1"
        /> All entries matched!
      </div>
    </b-card>

    <!-- Report 6: Supplier Credit vs VDPL Debit -->
    <b-card
      v-if="activeData.company_credit_vs_my_debit"
      class="mb-0 result-card"
    >
      <template #header>
        <h5 class="mb-0">
          6. Supplier Credit vs VDPL Debit
        </h5>
        <small class="text-muted">
          Total: {{ activeData.company_credit_vs_my_debit.total }} |
          Matched: {{ activeData.company_credit_vs_my_debit.matched }} |
          <span class="text-danger font-weight-bold">Unmatched: {{ activeData.company_credit_vs_my_debit.unmatched }}</span>
        </small>
      </template>
      <div
        v-if="activeData.company_credit_vs_my_debit.unmatched > 0"
        class="table-responsive"
      >
        <table class="table table-bordered table-sm compact-table mb-0">
          <thead>
            <tr>
              <th>Excel Row</th>
              <th>Reference</th>
              <th>Date</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Balance</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in activeData.company_credit_vs_my_debit.unmatched_entries"
              :key="`r6-${index}`"
            >
              <td>{{ row.excel_row }}</td>
              <td>{{ getCompanyReference(row) }}</td>
              <td>{{ getCompanyDate(row) }}</td>
              <td class="text-right">
                {{ row.debit ? formatAmount(row.debit) : '' }}
              </td>
              <td class="text-right">
                {{ row.credit ? formatAmount(row.credit) : '' }}
              </td>
              <td class="text-right">
                {{ row['4'] || '' }}
              </td>
              <td>{{ row['5'] || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-else
        class="text-center text-success py-3"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="24"
          class="mr-1"
        /> All entries matched!
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BButton, BSpinner, BButtonGroup,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CompareLedgerReports',
  components: {
    BCard,
    BButton,
    BSpinner,
    BButtonGroup,
  },
  props: {
    comparisonResult: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeTab: 'normal',
      printingCombined: false,
      printingReports: false,

      // Report 1 summation state
      report1SelectedCompany: {}, // { rowId: { amount, rowData } }
      report1SelectedVdpl: {}, // { rowId: { amount, rowData } }
      report1MatchedPairs: [], // [{ total, companyIds: [], vdplIds: [], rows: [] }]
      report1HiddenCompanyIds: [],
      report1HiddenVdplIds: [],
      report1PairsExpanded: true,

      // Report 2 summation state
      report2SelectedCompany: {},
      report2SelectedVdpl: {},
      report2MatchedPairs: [],
      report2HiddenCompanyIds: [],
      report2HiddenVdplIds: [],
      report2PairsExpanded: true,
    }
  },
  computed: {
    activeData() {
      if (!this.comparisonResult) return {}
      if (this.activeTab === 'rounded') {
        return {
          my_credit_vs_company_debit: this.comparisonResult.my_credit_vs_company_debit_rounded,
          my_debit_vs_company_credit: this.comparisonResult.my_debit_vs_company_credit_rounded,
          company_debit_vs_my_credit: this.comparisonResult.company_debit_vs_my_credit_rounded,
          company_credit_vs_my_debit: this.comparisonResult.company_credit_vs_my_debit_rounded,
        }
      }
      return {
        my_credit_vs_company_debit: this.comparisonResult.my_credit_vs_company_debit,
        my_debit_vs_company_credit: this.comparisonResult.my_debit_vs_company_credit,
        company_debit_vs_my_credit: this.comparisonResult.company_debit_vs_my_credit,
        company_credit_vs_my_debit: this.comparisonResult.company_credit_vs_my_debit,
      }
    },
    vdplCreditUnmatchedCount() {
      return this.activeData.my_credit_vs_company_debit?.unmatched || 0
    },
    companyDebitUnmatchedCount() {
      return this.activeData.company_debit_vs_my_credit?.unmatched || 0
    },
    vdplDebitUnmatchedCount() {
      return this.activeData.my_debit_vs_company_credit?.unmatched || 0
    },
    companyCreditUnmatchedCount() {
      return this.activeData.company_credit_vs_my_debit?.unmatched || 0
    },

    // Report 1 combined rows with row IDs
    combinedCreditDebitRows() {
      const vdplEntries = this.activeData.my_credit_vs_company_debit?.unmatched_entries || []
      const companyEntries = this.activeData.company_debit_vs_my_credit?.unmatched_entries || []
      const maxLen = Math.max(vdplEntries.length, companyEntries.length)
      const rows = []
      for (let i = 0; i < maxLen; i += 1) {
        const vdpl = vdplEntries[i] || {}
        const company = companyEntries[i] || {}
        rows.push({
          vdpl_row_id: vdpl.excel_row ? `v-${vdpl.excel_row}-${i}` : null,
          vdpl_excel_row: vdpl.excel_row || '',
          vdpl_credit: vdpl.Credit || null,
          vdpl_date: vdpl.Date || '',
          vdpl_raw: vdpl,
          company_row_id: company.excel_row ? `c-${company.excel_row}-${i}` : null,
          company_excel_row: company.excel_row || '',
          company_debit: company.debit || null,
          company_date: this.getCompanyDate(company),
          company_raw: company,
        })
      }
      return rows
    },

    // Report 2 combined rows with row IDs
    combinedDebitCreditRows() {
      const vdplEntries = this.activeData.my_debit_vs_company_credit?.unmatched_entries || []
      const companyEntries = this.activeData.company_credit_vs_my_debit?.unmatched_entries || []
      const maxLen = Math.max(vdplEntries.length, companyEntries.length)
      const rows = []
      for (let i = 0; i < maxLen; i += 1) {
        const vdpl = vdplEntries[i] || {}
        const company = companyEntries[i] || {}
        rows.push({
          vdpl_row_id: vdpl.excel_row ? `v-${vdpl.excel_row}-${i}` : null,
          vdpl_excel_row: vdpl.excel_row || '',
          vdpl_debit: vdpl.Debit || null,
          vdpl_date: vdpl.Date || '',
          vdpl_raw: vdpl,
          company_row_id: company.excel_row ? `c-${company.excel_row}-${i}` : null,
          company_excel_row: company.excel_row || '',
          company_credit: company.credit || null,
          company_date: this.getCompanyDate(company),
          company_raw: company,
        })
      }
      return rows
    },

    // Visible rows for Report 1 — filter each side independently, then re-zip
    visibleCombinedCreditDebitRows() {
      const allVdplEntries = this.activeData.my_credit_vs_company_debit?.unmatched_entries || []
      const allCompanyEntries = this.activeData.company_debit_vs_my_credit?.unmatched_entries || []

      // Filter out hidden entries from each side
      const visibleCompany = allCompanyEntries
        .map((entry, i) => ({ entry, rowId: entry.excel_row ? `c-${entry.excel_row}-${i}` : null, idx: i }))
        .filter(item => !item.rowId || !this.report1HiddenCompanyIds.includes(item.rowId))

      const visibleVdpl = allVdplEntries
        .map((entry, i) => ({ entry, rowId: entry.excel_row ? `v-${entry.excel_row}-${i}` : null, idx: i }))
        .filter(item => !item.rowId || !this.report1HiddenVdplIds.includes(item.rowId))

      // Re-zip remaining entries side by side
      const maxLen = Math.max(visibleCompany.length, visibleVdpl.length)
      const rows = []
      for (let i = 0; i < maxLen; i += 1) {
        const company = visibleCompany[i] || {}
        const vdpl = visibleVdpl[i] || {}
        const cEntry = company.entry || {}
        const vEntry = vdpl.entry || {}
        rows.push({
          company_row_id: company.rowId || null,
          company_excel_row: cEntry.excel_row || '',
          company_debit: cEntry.debit || null,
          company_date: this.getCompanyDate(cEntry),
          company_raw: cEntry,
          vdpl_row_id: vdpl.rowId || null,
          vdpl_excel_row: vEntry.excel_row || '',
          vdpl_credit: vEntry.Credit || null,
          vdpl_date: vEntry.Date || '',
          vdpl_raw: vEntry,
        })
      }
      return rows
    },

    // Visible rows for Report 2 — filter each side independently, then re-zip
    visibleCombinedDebitCreditRows() {
      const allVdplEntries = this.activeData.my_debit_vs_company_credit?.unmatched_entries || []
      const allCompanyEntries = this.activeData.company_credit_vs_my_debit?.unmatched_entries || []

      const visibleCompany = allCompanyEntries
        .map((entry, i) => ({ entry, rowId: entry.excel_row ? `c-${entry.excel_row}-${i}` : null, idx: i }))
        .filter(item => !item.rowId || !this.report2HiddenCompanyIds.includes(item.rowId))

      const visibleVdpl = allVdplEntries
        .map((entry, i) => ({ entry, rowId: entry.excel_row ? `v-${entry.excel_row}-${i}` : null, idx: i }))
        .filter(item => !item.rowId || !this.report2HiddenVdplIds.includes(item.rowId))

      const maxLen = Math.max(visibleCompany.length, visibleVdpl.length)
      const rows = []
      for (let i = 0; i < maxLen; i += 1) {
        const company = visibleCompany[i] || {}
        const vdpl = visibleVdpl[i] || {}
        const cEntry = company.entry || {}
        const vEntry = vdpl.entry || {}
        rows.push({
          company_row_id: company.rowId || null,
          company_excel_row: cEntry.excel_row || '',
          company_credit: cEntry.credit || null,
          company_date: this.getCompanyDate(cEntry),
          company_raw: cEntry,
          vdpl_row_id: vdpl.rowId || null,
          vdpl_excel_row: vEntry.excel_row || '',
          vdpl_debit: vEntry.Debit || null,
          vdpl_date: vEntry.Date || '',
          vdpl_raw: vEntry,
        })
      }
      return rows
    },

    // Visible unmatched counts
    visibleCompanyDebitUnmatchedCount() {
      return this.visibleCombinedCreditDebitRows.filter(r => r.company_row_id && r.company_debit).length
    },
    visibleVdplCreditUnmatchedCount() {
      return this.visibleCombinedCreditDebitRows.filter(r => r.vdpl_row_id && r.vdpl_credit).length
    },
    visibleCompanyCreditUnmatchedCount() {
      return this.visibleCombinedDebitCreditRows.filter(r => r.company_row_id && r.company_credit).length
    },
    visibleVdplDebitUnmatchedCount() {
      return this.visibleCombinedDebitCreditRows.filter(r => r.vdpl_row_id && r.vdpl_debit).length
    },

    // Report 1 totals
    report1SupplierTotal() {
      return Object.values(this.report1SelectedCompany).reduce((sum, item) => sum + item.amount, 0)
    },
    report1VdplTotal() {
      return Object.values(this.report1SelectedVdpl).reduce((sum, item) => sum + item.amount, 0)
    },
    report1Matched() {
      if (this.report1SupplierTotal === 0 && this.report1VdplTotal === 0) return false
      return Math.abs(this.report1SupplierTotal - this.report1VdplTotal) < 0.01
    },
    report1HideEnabled() {
      return this.report1Matched
    },

    // Report 2 totals
    report2SupplierTotal() {
      return Object.values(this.report2SelectedCompany).reduce((sum, item) => sum + item.amount, 0)
    },
    report2VdplTotal() {
      return Object.values(this.report2SelectedVdpl).reduce((sum, item) => sum + item.amount, 0)
    },
    report2Matched() {
      if (this.report2SupplierTotal === 0 && this.report2VdplTotal === 0) return false
      return Math.abs(this.report2SupplierTotal - this.report2VdplTotal) < 0.01
    },
    report2HideEnabled() {
      return this.report2Matched
    },
  },
  watch: {
    activeTab() {
      // Reset summation state when switching tabs
      this.resetReport1()
      this.resetReport2()
      // Keep matched pairs - they were already committed
    },
  },
  methods: {
    // ===== Report 1 Summation Methods =====
    isReport1CompanySelected(rowId) {
      return rowId && !!this.report1SelectedCompany[rowId]
    },
    isReport1VdplSelected(rowId) {
      return rowId && !!this.report1SelectedVdpl[rowId]
    },
    toggleReport1Company(row) {
      if (!row.company_row_id || !row.company_debit) return
      const id = row.company_row_id
      if (this.report1SelectedCompany[id]) {
        this.$delete(this.report1SelectedCompany, id)
      } else {
        this.$set(this.report1SelectedCompany, id, {
          amount: parseFloat(row.company_debit) || 0,
          rowData: row,
        })
      }
    },
    toggleReport1Vdpl(row) {
      if (!row.vdpl_row_id || !row.vdpl_credit) return
      const id = row.vdpl_row_id
      if (this.report1SelectedVdpl[id]) {
        this.$delete(this.report1SelectedVdpl, id)
      } else {
        this.$set(this.report1SelectedVdpl, id, {
          amount: parseFloat(row.vdpl_credit) || 0,
          rowData: row,
        })
      }
    },
    getReport1CompanyCellClass(rowId) {
      if (!rowId) return ''
      if (this.report1Matched && this.report1SelectedCompany[rowId]) return 'text-success font-weight-bold'
      if (this.report1SelectedCompany[rowId]) return 'text-danger font-weight-bold'
      return ''
    },
    getReport1VdplCellClass(rowId) {
      if (!rowId) return ''
      if (this.report1Matched && this.report1SelectedVdpl[rowId]) return 'text-success font-weight-bold'
      if (this.report1SelectedVdpl[rowId]) return 'text-danger font-weight-bold'
      return ''
    },
    hideMatchedReport1() {
      if (!this.report1Matched) return

      // Build matched pair record
      const companyIds = Object.keys(this.report1SelectedCompany)
      const vdplIds = Object.keys(this.report1SelectedVdpl)

      // Collect all row data for the pair, combining supplier + vdpl side by side
      const companyRows = companyIds.map(id => this.report1SelectedCompany[id].rowData)
      const vdplRows = vdplIds.map(id => this.report1SelectedVdpl[id].rowData)
      const maxLen = Math.max(companyRows.length, vdplRows.length)
      const pairRows = []
      for (let i = 0; i < maxLen; i += 1) {
        const c = companyRows[i] || {}
        const v = vdplRows[i] || {}
        pairRows.push({
          company_excel_row: c.company_excel_row || '',
          company_debit: c.company_debit || null,
          company_date: c.company_date || '',
          vdpl_excel_row: v.vdpl_excel_row || '',
          vdpl_credit: v.vdpl_credit || null,
          vdpl_date: v.vdpl_date || '',
        })
      }

      this.report1MatchedPairs.push({
        total: this.report1SupplierTotal,
        companyIds: [...companyIds],
        vdplIds: [...vdplIds],
        rows: pairRows,
      })

      // Hide these rows
      this.report1HiddenCompanyIds.push(...companyIds)
      this.report1HiddenVdplIds.push(...vdplIds)

      // Clear current selection for next round
      this.report1SelectedCompany = {}
      this.report1SelectedVdpl = {}
    },
    resetReport1() {
      this.report1SelectedCompany = {}
      this.report1SelectedVdpl = {}
    },

    // ===== Report 2 Summation Methods =====
    isReport2CompanySelected(rowId) {
      return rowId && !!this.report2SelectedCompany[rowId]
    },
    isReport2VdplSelected(rowId) {
      return rowId && !!this.report2SelectedVdpl[rowId]
    },
    toggleReport2Company(row) {
      if (!row.company_row_id || !row.company_credit) return
      const id = row.company_row_id
      if (this.report2SelectedCompany[id]) {
        this.$delete(this.report2SelectedCompany, id)
      } else {
        this.$set(this.report2SelectedCompany, id, {
          amount: parseFloat(row.company_credit) || 0,
          rowData: row,
        })
      }
    },
    toggleReport2Vdpl(row) {
      if (!row.vdpl_row_id || !row.vdpl_debit) return
      const id = row.vdpl_row_id
      if (this.report2SelectedVdpl[id]) {
        this.$delete(this.report2SelectedVdpl, id)
      } else {
        this.$set(this.report2SelectedVdpl, id, {
          amount: parseFloat(row.vdpl_debit) || 0,
          rowData: row,
        })
      }
    },
    getReport2CompanyCellClass(rowId) {
      if (!rowId) return ''
      if (this.report2Matched && this.report2SelectedCompany[rowId]) return 'text-success font-weight-bold'
      if (this.report2SelectedCompany[rowId]) return 'text-danger font-weight-bold'
      return ''
    },
    getReport2VdplCellClass(rowId) {
      if (!rowId) return ''
      if (this.report2Matched && this.report2SelectedVdpl[rowId]) return 'text-success font-weight-bold'
      if (this.report2SelectedVdpl[rowId]) return 'text-danger font-weight-bold'
      return ''
    },
    hideMatchedReport2() {
      if (!this.report2Matched) return

      const companyIds = Object.keys(this.report2SelectedCompany)
      const vdplIds = Object.keys(this.report2SelectedVdpl)

      const companyRows = companyIds.map(id => this.report2SelectedCompany[id].rowData)
      const vdplRows = vdplIds.map(id => this.report2SelectedVdpl[id].rowData)
      const maxLen = Math.max(companyRows.length, vdplRows.length)
      const pairRows = []
      for (let i = 0; i < maxLen; i += 1) {
        const c = companyRows[i] || {}
        const v = vdplRows[i] || {}
        pairRows.push({
          company_excel_row: c.company_excel_row || '',
          company_credit: c.company_credit || null,
          company_date: c.company_date || '',
          vdpl_excel_row: v.vdpl_excel_row || '',
          vdpl_debit: v.vdpl_debit || null,
          vdpl_date: v.vdpl_date || '',
        })
      }

      this.report2MatchedPairs.push({
        total: this.report2SupplierTotal,
        companyIds: [...companyIds],
        vdplIds: [...vdplIds],
        rows: pairRows,
      })

      this.report2HiddenCompanyIds.push(...companyIds)
      this.report2HiddenVdplIds.push(...vdplIds)

      this.report2SelectedCompany = {}
      this.report2SelectedVdpl = {}
    },
    resetReport2() {
      this.report2SelectedCompany = {}
      this.report2SelectedVdpl = {}
    },

    // ===== Existing Helper Methods =====
    getCompanyDate(row) {
      if (!row || (!row.date && !row['0'])) return ''
      if (row.date && row.date !== 'NaN' && row.date !== null) {
        return this.formatCompanyDate(row.date)
      }
      if (row['0']) {
        const dateMatch = row['0'].match(/(\d{2}-[A-Za-z]{3}-\d{4})/)
        if (dateMatch) return dateMatch[1]
      }
      return ''
    },
    getCompanyReference(row) {
      if (!row || !row['0']) return ''
      const refStr = row['0']
      const cleaned = refStr.replace(/\s+\d{2}-[A-Za-z]{3}-\d{4}/, '').trim()
      const parts = cleaned.split(/\s+/)
      if (parts.length >= 2 && parts[0] === parts[1]) return parts[0]
      return cleaned
    },
    formatCompanyDate(dateString) {
      if (!dateString || dateString === 'NaN' || dateString === null) return ''
      if (typeof dateString === 'string' && (dateString.includes('T') || dateString.includes(' '))) {
        try {
          const date = new Date(dateString)
          if (!Number.isNaN(date.getTime())) {
            return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          }
        } catch (e) {
          return dateString
        }
      }
      return dateString
    },
    formatAmount(amount) {
      if (!amount || amount === 0 || amount === '0' || amount === '0.0') return ''
      const numAmount = parseFloat(amount)
      if (Number.isNaN(numAmount)) return amount
      return numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    // ===== Print Methods =====
    getPrintStyles() {
      return `
        <style>
          @media print {
            * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
            body { margin: 15px; font-family: Arial, sans-serif; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            th, td { page-break-inside: avoid; }
          }
          @page { margin: 0.5in; size: A4 landscape; }
          body { font-family: Arial, sans-serif; margin: 15px; }
          h1 { text-align: center; margin-bottom: 20px; font-size: 24px; }
          h2 { margin-top: 15px; margin-bottom: 5px; font-size: 16px; background-color: #007bff; color: white; padding: 6px 8px; border-radius: 4px; }
          h2.combined { background-color: #6f42c1; }
          h3.matched-title { margin-top: 10px; margin-bottom: 5px; font-size: 14px; color: #28a745; border-bottom: 2px solid #28a745; padding-bottom: 3px; }
          .pair-label-print { font-size: 12px; font-weight: bold; color: #28a745; margin: 5px 0 3px 0; }
          .summary { background-color: #f8f9fa; padding: 5px 8px; margin-bottom: 5px; border-radius: 4px; border: 1px solid #dee2e6; font-size: 11px; }
          .text-danger { color: #dc3545 !important; font-weight: bold; }
          .text-success { color: #28a745 !important; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          th { background-color: #e9ecef !important; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #000; padding: 6px; text-align: left; }
          th.vdpl-header { background-color: #d1ecf1 !important; }
          th.company-header { background-color: #fff3cd !important; }
          td { font-size: 9px; border: 1px solid #000; padding: 4px; }
          td.matched-cell { color: #28a745 !important; font-weight: bold; background-color: #d4edda !important; }
          tr:nth-child(even) td { background-color: #f8f8f8 !important; }
          tr:nth-child(odd) td { background-color: #ffffff !important; }
          tr.matched-print-row td { background-color: #d4edda !important; color: #28a745 !important; font-weight: bold; }
          .text-right { text-align: right; }
          .no-data { text-align: center; padding: 15px; color: #28a745; font-weight: bold; font-size: 11px; margin-bottom: 10px; }
          .mode-badge { display: inline-block; background-color: #6c757d; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; margin-left: 10px; }
        </style>`
    },
    buildVdplReportHTML(title, reportNum, data) {
      let html = `<h2>${reportNum}. ${title}</h2>`
      html += `<div class="summary"><strong>Total:</strong> ${data.total} | <strong>Matched:</strong> ${data.matched} | <span class="text-danger">Unmatched: ${data.unmatched}</span></div>`
      if (data.unmatched > 0 && data.unmatched_entries?.length > 0) {
        html += '<table><thead><tr><th>Excel Row</th><th>Date</th><th>Particulars</th><th>Debit</th><th>Credit</th><th>Balance</th></tr></thead><tbody>'
        data.unmatched_entries.forEach(row => {
          html += `<tr><td>${row.excel_row || ''}</td><td>${row.Date || ''}</td><td>${row.Particulars || ''}</td><td class="text-right">${row.Debit ? this.formatAmount(row.Debit) : ''}</td><td class="text-right">${row.Credit ? this.formatAmount(row.Credit) : ''}</td><td class="text-right">${row.Balance ? this.formatAmount(row.Balance) : ''}</td></tr>`
        })
        html += '</tbody></table>'
      } else {
        html += '<div class="no-data">All entries matched!</div>'
      }
      return html
    },
    buildCompanyReportHTML(title, reportNum, data) {
      let html = `<h2>${reportNum}. ${title}</h2>`
      html += `<div class="summary"><strong>Total:</strong> ${data.total} | <strong>Matched:</strong> ${data.matched} | <span class="text-danger">Unmatched: ${data.unmatched}</span></div>`
      if (data.unmatched > 0 && data.unmatched_entries?.length > 0) {
        html += '<table><thead><tr><th>Excel Row</th><th>Reference</th><th>Date</th><th>Debit</th><th>Credit</th><th>Balance</th><th>Type</th></tr></thead><tbody>'
        data.unmatched_entries.forEach(row => {
          html += `<tr><td>${row.excel_row}</td><td>${this.getCompanyReference(row)}</td><td>${this.getCompanyDate(row)}</td><td class="text-right">${row.debit ? this.formatAmount(row.debit) : ''}</td><td class="text-right">${row.credit ? this.formatAmount(row.credit) : ''}</td><td class="text-right">${row['4'] || ''}</td><td>${row['5'] || ''}</td></tr>`
        })
        html += '</tbody></table>'
      } else {
        html += '<div class="no-data">All entries matched!</div>'
      }
      return html
    },
    buildCombinedCreditDebitHTML() {
      let html = '<h2 class="combined">1. Supplier Debit + VDPL Credit (Combined Unmatched)</h2>'
      html += `<div class="summary"><strong>Supplier Unmatched:</strong> ${this.visibleCompanyDebitUnmatchedCount} | <strong>VDPL Unmatched:</strong> ${this.visibleVdplCreditUnmatchedCount} | <strong>Total: ${this.visibleCompanyDebitUnmatchedCount + this.visibleVdplCreditUnmatchedCount}</strong></div>`
      if (this.visibleCombinedCreditDebitRows.length > 0) {
        html += '<table><thead><tr><th class="company-header" colspan="3">Supplier Debit (Report 5)</th><th class="vdpl-header" colspan="3">VDPL Credit (Report 3)</th></tr>'
        html += '<tr><th class="company-header">Excel Row</th><th class="company-header text-right">Debit</th><th class="company-header">Date</th><th class="vdpl-header">Excel Row</th><th class="vdpl-header text-right">Credit</th><th class="vdpl-header">Date</th></tr></thead><tbody>'
        this.visibleCombinedCreditDebitRows.forEach(row => {
          html += `<tr><td>${row.company_excel_row}</td><td class="text-right">${row.company_debit ? this.formatAmount(row.company_debit) : ''}</td><td>${row.company_date}</td><td>${row.vdpl_excel_row}</td><td class="text-right">${row.vdpl_credit ? this.formatAmount(row.vdpl_credit) : ''}</td><td>${row.vdpl_date}</td></tr>`
        })
        html += '</tbody></table>'
      } else {
        html += '<div class="no-data">All entries matched!</div>'
      }

      // Print matched pairs
      if (this.report1MatchedPairs.length > 0) {
        html += '<h3 class="matched-title">Matched Pairs</h3>'
        this.report1MatchedPairs.forEach((pair, idx) => {
          html += `<div class="pair-label-print">Pair ${idx + 1} — ${this.formatAmount(pair.total)}</div>`
          html += '<table><thead><tr><th class="company-header" colspan="3">Supplier Debit</th><th class="vdpl-header" colspan="3">VDPL Credit</th></tr></thead><tbody>'
          pair.rows.forEach(mrow => {
            html += `<tr class="matched-print-row"><td class="matched-cell">${mrow.company_excel_row}</td><td class="text-right matched-cell">${mrow.company_debit ? this.formatAmount(mrow.company_debit) : ''}</td><td class="matched-cell">${mrow.company_date}</td><td class="matched-cell">${mrow.vdpl_excel_row}</td><td class="text-right matched-cell">${mrow.vdpl_credit ? this.formatAmount(mrow.vdpl_credit) : ''}</td><td class="matched-cell">${mrow.vdpl_date}</td></tr>`
          })
          html += '</tbody></table>'
        })
      }
      return html
    },
    buildCombinedDebitCreditHTML() {
      let html = '<h2 class="combined">2. Supplier Credit + VDPL Debit (Combined Unmatched)</h2>'
      html += `<div class="summary"><strong>Supplier Unmatched:</strong> ${this.visibleCompanyCreditUnmatchedCount} | <strong>VDPL Unmatched:</strong> ${this.visibleVdplDebitUnmatchedCount} | <strong>Total: ${this.visibleCompanyCreditUnmatchedCount + this.visibleVdplDebitUnmatchedCount}</strong></div>`
      if (this.visibleCombinedDebitCreditRows.length > 0) {
        html += '<table><thead><tr><th class="company-header" colspan="3">Supplier Credit (Report 6)</th><th class="vdpl-header" colspan="3">VDPL Debit (Report 4)</th></tr>'
        html += '<tr><th class="company-header">Excel Row</th><th class="company-header text-right">Credit</th><th class="company-header">Date</th><th class="vdpl-header">Excel Row</th><th class="vdpl-header text-right">Debit</th><th class="vdpl-header">Date</th></tr></thead><tbody>'
        this.visibleCombinedDebitCreditRows.forEach(row => {
          html += `<tr><td>${row.company_excel_row}</td><td class="text-right">${row.company_credit ? this.formatAmount(row.company_credit) : ''}</td><td>${row.company_date}</td><td>${row.vdpl_excel_row}</td><td class="text-right">${row.vdpl_debit ? this.formatAmount(row.vdpl_debit) : ''}</td><td>${row.vdpl_date}</td></tr>`
        })
        html += '</tbody></table>'
      } else {
        html += '<div class="no-data">All entries matched!</div>'
      }

      // Print matched pairs
      if (this.report2MatchedPairs.length > 0) {
        html += '<h3 class="matched-title">Matched Pairs</h3>'
        this.report2MatchedPairs.forEach((pair, idx) => {
          html += `<div class="pair-label-print">Pair ${idx + 1} — ${this.formatAmount(pair.total)}</div>`
          html += '<table><thead><tr><th class="company-header" colspan="3">Supplier Credit</th><th class="vdpl-header" colspan="3">VDPL Debit</th></tr></thead><tbody>'
          pair.rows.forEach(mrow => {
            html += `<tr class="matched-print-row"><td class="matched-cell">${mrow.company_excel_row}</td><td class="text-right matched-cell">${mrow.company_credit ? this.formatAmount(mrow.company_credit) : ''}</td><td class="matched-cell">${mrow.company_date}</td><td class="matched-cell">${mrow.vdpl_excel_row}</td><td class="text-right matched-cell">${mrow.vdpl_debit ? this.formatAmount(mrow.vdpl_debit) : ''}</td><td class="matched-cell">${mrow.vdpl_date}</td></tr>`
          })
          html += '</tbody></table>'
        })
      }
      return html
    },
    printHTML(htmlContent) {
      return new Promise((resolve, reject) => {
        try {
          const iframe = document.createElement('iframe')
          iframe.style.position = 'absolute'
          iframe.style.top = '-9999px'
          iframe.style.left = '-9999px'
          document.body.appendChild(iframe)
          iframe.contentDocument.open()
          iframe.contentDocument.write(htmlContent)
          iframe.contentDocument.close()

          setTimeout(() => {
            iframe.contentWindow.print()
            iframe.contentWindow.addEventListener('afterprint', () => {
              if (document.body.contains(iframe)) document.body.removeChild(iframe)
              resolve('Print completed successfully')
            })
            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
                resolve('Print completed')
              }
            }, 500)
          }, 500)
        } catch (error) {
          reject(error)
        }
      })
    },
    async handlePrintCombined() {
      this.printingCombined = true
      try {
        const modeLabel = this.activeTab === 'rounded' ? 'Rounded' : 'Normal'
        let printHTML = `<!DOCTYPE html><html><head>${this.getPrintStyles()}</head><body>`
        printHTML += `<h1>Ledger Comparison - Combined <span class="mode-badge">${modeLabel}</span></h1>`
        printHTML += this.buildCombinedCreditDebitHTML()
        printHTML += this.buildCombinedDebitCreditHTML()
        printHTML += '</body></html>'
        await this.printHTML(printHTML)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.message || 'Failed to print!', icon: 'AlertCircleIcon', variant: 'danger' },
        })
      } finally {
        this.printingCombined = false
      }
    },
    async handlePrintReports() {
      this.printingReports = true
      try {
        const modeLabel = this.activeTab === 'rounded' ? 'Rounded' : 'Normal'
        let printHTML = `<!DOCTYPE html><html><head>${this.getPrintStyles()}</head><body>`
        printHTML += `<h1>Ledger Comparison - Reports 3-6 <span class="mode-badge">${modeLabel}</span></h1>`

        if (this.activeData.my_credit_vs_company_debit) {
          printHTML += this.buildVdplReportHTML('VDPL Credit vs Supplier Debit', 3, this.activeData.my_credit_vs_company_debit)
        }
        if (this.activeData.my_debit_vs_company_credit) {
          printHTML += this.buildVdplReportHTML('VDPL Debit vs Supplier Credit', 4, this.activeData.my_debit_vs_company_credit)
        }
        if (this.activeData.company_debit_vs_my_credit) {
          printHTML += this.buildCompanyReportHTML('Supplier Debit vs VDPL Credit', 5, this.activeData.company_debit_vs_my_credit)
        }
        if (this.activeData.company_credit_vs_my_debit) {
          printHTML += this.buildCompanyReportHTML('Supplier Credit vs VDPL Debit', 6, this.activeData.company_credit_vs_my_debit)
        }

        printHTML += '</body></html>'
        await this.printHTML(printHTML)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.message || 'Failed to print!', icon: 'AlertCircleIcon', variant: 'danger' },
        })
      } finally {
        this.printingReports = false
      }
    },
  },
}
</script>

<style scoped>
.compact-table { font-size: 0.875rem; }
.compact-table th, .compact-table td { padding: 0.4rem; vertical-align: middle; }
.compact-table th { font-weight: 600; background-color: #f8f9fa; }
.text-right { text-align: right; }
.result-card { margin-bottom: 0.5rem !important; }
.result-card .card-body { padding-bottom: 0.5rem !important; }
.bg-info-light { background-color: #d1ecf1 !important; color: #0c5460 !important; font-weight: 600; }
.bg-warning-light { background-color: #fff3cd !important; color: #856404 !important; font-weight: 600; }
.combined-report .card-header { background-color: #f3f0ff; border-left: 4px solid #6f42c1; }
.combined-wrapper { border: 1px solid #dee2e6; border-radius: 6px; padding: 1rem; }

/* Fixed height scrollable table container */
.table-scroll-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}
.table-scroll-container table { margin-bottom: 0 !important; }
.table-scroll-container thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: 0 1px 0 #dee2e6;
}

/* Summation Feature Styles */
.th-action { width: 30px; min-width: 30px; max-width: 30px; padding: 0.2rem !important; }
.action-cell { width: 30px; min-width: 30px; max-width: 30px; padding: 0.2rem !important; }
.action-icon {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  color: #007bff;
}
.action-icon:hover { transform: scale(1.2); }
.action-icon.text-danger { color: #dc3545 !important; }

.summation-bar {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9rem;
}
.summation-value {
  font-size: 1rem;
  min-width: 80px;
  display: inline-block;
}

.mt-half { margin-top: 0.25rem; }
.mr-half { margin-right: 0.25rem; }
.py-half { padding-top: 0.35rem; padding-bottom: 0.35rem; }

/* Matched Pairs */
.matched-pairs-section {
  border: 1px solid #28a745;
  border-radius: 6px;
  overflow: hidden;
}
.matched-pairs-header {
  background: #d4edda;
  border-bottom: 1px solid #28a745;
}
.cursor-pointer { cursor: pointer; }
.matched-pair-card {
  margin: 0.5rem;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  overflow: hidden;
}
.pair-label {
  background: #e8f5e9;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
  font-size: 0.8rem;
  color: #28a745;
  border-bottom: 1px solid #c3e6cb;
}
.matched-table td { color: #28a745; font-weight: 500; }
.matched-row td { background-color: #f0fff0 !important; }
</style>
